/**
 * Risk & AML Dashboard
 *
 * Real-time monitoring of betting risk across all markets
 * Includes: Dashboard, Risk Cases, Network Analysis, AML Review
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useAdmin } from '../store/AdminContext';
import { apiService } from '../services/apiService';
import './RiskPage.css';

interface RiskPageProps {
  view: string;
}

interface GlobalStats {
  totalPoolAcrossMarkets: number;
  totalLiabilityAcrossMarkets: number;
  liveMarketCount: number;
  activeBetCount: number;
  activeReserveCount: number;
  pendingSwitchOfferCount: number;
}

interface MarketRisk {
  id: string;
  headline: string;
  status: string;
  totalPool: number;
  totalLiability: number;
  maxPotentialLoss: number;
  marginEstimate: number;
  outcomeCount: number;
  activeBetCount: number;
  healthStatus: 'healthy' | 'warning' | 'critical';
  issues: string[];
  warnings: string[];
}

interface TopUser {
  userId: string;
  username: string;
  totalStaked: number;
  potentialPayout: number;
  activeBetCount: number;
}

interface RecentActivity {
  id: string;
  type: string;
  userId: string;
  marketId: string;
  marketHeadline: string;
  amount: number;
  description: string;
  createdAt: string;
}

interface FrozenUser {
  id: string;
  email: string;
  username: string;
  frozen_at: string | null;
}

interface Withdrawal {
  id: string;
  userId: string;
  amount: number;
  status: string;
  userEmail: string;
  username: string;
  createdAt: string;
}

interface AmlAlert {
  id: string;
  checkId: string;
  status: string;
  userEmail?: string;
  username?: string;
  riskLevel?: string;
  triggerType?: string;
  createdAt: string;
}

const RiskPage: React.FC<RiskPageProps> = ({ view }) => {
  const { setCurrentPage, setCurrentPanel } = useAdmin();
  const [stats, setStats] = useState<GlobalStats | null>(null);
  const [markets, setMarkets] = useState<MarketRisk[]>([]);
  const [topUsers, setTopUsers] = useState<TopUser[]>([]);
  const [activity, setActivity] = useState<RecentActivity[]>([]);
  const [frozenUsers, setFrozenUsers] = useState<FrozenUser[]>([]);
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [amlAlerts, setAmlAlerts] = useState<AmlAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMarket, setSelectedMarket] = useState<string | null>(null);

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsRes, marketsRes, topUsersRes, activityRes] = await Promise.all([
        apiService.getRiskOverview(),
        apiService.getRiskMarkets(),
        apiService.getTopExposure(10),
        apiService.getRiskActivity({ limit: 20 }),
      ]);

      setStats(statsRes.stats);
      setMarkets(marketsRes.markets);
      setTopUsers(topUsersRes.users);
      setActivity(activityRes.activity);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load risk dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRiskCases = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [marketsRes, topUsersRes, usersRes] = await Promise.all([
        apiService.getRiskMarkets(),
        apiService.getTopExposure(20),
        apiService.getAllUsers({ filter: 'banned' }),
      ]);
      setMarkets(marketsRes.markets);
      setTopUsers(topUsersRes.users);
      setFrozenUsers(usersRes.users || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load risk cases');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadNetworkData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [activityRes, topUsersRes, marketsRes] = await Promise.all([
        apiService.getRiskActivity({ limit: 100 }),
        apiService.getTopExposure(15),
        apiService.getRiskMarkets(),
      ]);
      setActivity(activityRes.activity);
      setTopUsers(topUsersRes.users);
      setMarkets(marketsRes.markets);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load network data');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAMLQueue = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [withdrawalsRes, usersRes, amlRes] = await Promise.all([
        apiService.getWithdrawals('pending'),
        apiService.getAllUsers({ filter: 'banned' }),
        apiService.getAmlQueue({ status: 'pending' }).catch(() => ({ alerts: [], total: 0 })),
      ]);
      setWithdrawals(withdrawalsRes.withdrawals || []);
      setFrozenUsers(usersRes.users || []);
      setAmlAlerts(amlRes.alerts || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load AML queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === 'risk-dashboard') {
      loadDashboard();
    } else if (view === 'risk-cases') {
      loadRiskCases();
    } else if (view === 'network-graph') {
      loadNetworkData();
    } else if (view === 'aml-queue') {
      loadAMLQueue();
    }
  }, [view, loadDashboard, loadRiskCases, loadNetworkData, loadAMLQueue]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getHealthBadge = (status: 'healthy' | 'warning' | 'critical') => {
    const classes = {
      healthy: 'health-badge healthy',
      warning: 'health-badge warning',
      critical: 'health-badge critical',
    };
    const labels = {
      healthy: '✓ Healthy',
      warning: '⚠ Warning',
      critical: '✕ Critical',
    };
    return <span className={classes[status]}>{labels[status]}</span>;
  };

  const viewTitles: Record<string, string> = {
    'risk-dashboard': 'Risk Dashboard',
    'risk-cases': 'Risk Cases',
    'network-graph': 'Network Analysis',
    'aml-queue': 'AML Review',
  };
  const viewTitle = viewTitles[view] || 'Risk & AML';
  const retryHandler = () => {
    if (view === 'risk-dashboard') loadDashboard();
    else if (view === 'risk-cases') loadRiskCases();
    else if (view === 'network-graph') loadNetworkData();
    else if (view === 'aml-queue') loadAMLQueue();
  };

  if (loading) {
    return (
      <div className="risk-page">
        <div className="page-header">
          <h1>{viewTitle}</h1>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="risk-page">
        <div className="page-header">
          <h1>{viewTitle}</h1>
        </div>
        <div className="error-container">
          <span>{error}</span>
          <button onClick={retryHandler} className="retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  // --- Risk Cases Screen ---
  if (view === 'risk-cases') {
    const criticalMarkets = markets.filter((m) => m.healthStatus === 'critical' || m.healthStatus === 'warning');
    const highExposureUsers = topUsers.slice(0, 10);
    return (
      <div className="risk-page">
        <div className="page-header">
          <div>
            <h1>Risk Cases</h1>
            <p className="page-sub">Active risk cases requiring review</p>
          </div>
          <button onClick={loadRiskCases} className="refresh-btn">↻ Refresh</button>
        </div>

        <div className="stats-grid risk-cases-stats">
          <div className="stat-card clickable" onClick={() => setCurrentPanel('risk')}>
            <span className="stat-label">Market Risk</span>
            <span className="stat-value danger">{criticalMarkets.length}</span>
            <span className="stat-hint">Critical / warning markets</span>
          </div>
          <div className="stat-card clickable" onClick={() => setCurrentPage('users-banned')}>
            <span className="stat-label">Frozen Accounts</span>
            <span className="stat-value orange">{frozenUsers.length}</span>
            <span className="stat-hint">Under investigation</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">High Exposure</span>
            <span className="stat-value">{highExposureUsers.length}</span>
            <span className="stat-hint">Top users by exposure</span>
          </div>
        </div>

        <div className="risk-cases-grid">
          <div className="dashboard-section">
            <h2>Market Risk Cases</h2>
            {criticalMarkets.length === 0 ? (
              <div className="empty-state">No critical or warning markets</div>
            ) : (
              <div className="risk-cases-list">
                {criticalMarkets.map((m) => (
                  <div key={m.id} className={`risk-case-item ${m.healthStatus}`}>
                    <div className="risk-case-icon">
                      {m.healthStatus === 'critical' ? '🚨' : '⚠️'}
                    </div>
                    <div className="risk-case-body">
                      <div className="risk-case-title">{m.headline}</div>
                      <div className="risk-case-meta">
                        Liability: {formatCurrency(m.totalLiability)} · Margin: {m.marginEstimate.toFixed(1)}%
                        {m.issues?.length ? ` · ${m.issues[0]}` : ''}
                      </div>
                    </div>
                    <div className="risk-case-actions">
                      <span className={`status-badge ${m.healthStatus}`}>{m.healthStatus}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="dashboard-section">
            <h2>Frozen Accounts</h2>
            {frozenUsers.length === 0 ? (
              <div className="empty-state">No frozen accounts</div>
            ) : (
              <div className="risk-cases-list">
                {frozenUsers.map((u) => (
                  <div key={u.id} className="risk-case-item frozen">
                    <div className="risk-case-icon">🚫</div>
                    <div className="risk-case-body">
                      <div className="risk-case-title">{u.username || u.email || u.id}</div>
                      <div className="risk-case-meta">
                        Frozen · {u.frozen_at ? formatDate(u.frozen_at) : '—'}
                      </div>
                    </div>
                    <div className="risk-case-actions">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => {
                          setCurrentPanel('master');
                          setCurrentPage('users-banned');
                        }}
                      >
                        View
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="dashboard-section">
            <h2>High Exposure Users</h2>
            {highExposureUsers.length === 0 ? (
              <div className="empty-state">No high exposure users</div>
            ) : (
              <div className="risk-cases-list">
                {highExposureUsers.map((u) => (
                  <div key={u.userId} className="risk-case-item exposure">
                    <div className="risk-case-icon">💰</div>
                    <div className="risk-case-body">
                      <div className="risk-case-title">{u.username || u.userId}</div>
                      <div className="risk-case-meta">
                        Staked: {formatCurrency(u.totalStaked)} → Payout: {formatCurrency(u.potentialPayout)} · {u.activeBetCount} bets
                      </div>
                    </div>
                    <div className="risk-case-actions">
                      <span className="stat-value small">{formatCurrency(u.potentialPayout)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- Network Analysis Screen ---
  if (view === 'network-graph') {
    const userMarketMap = new Map<string, Set<string>>();
    activity.forEach((a) => {
      if (!userMarketMap.has(a.userId)) userMarketMap.set(a.userId, new Set());
      userMarketMap.get(a.userId)!.add(a.marketId);
    });
    const userList = Array.from(userMarketMap.entries()).slice(0, 12);
    const edges: Array<{ u1: string; u2: string; market: string }> = [];
    for (let i = 0; i < userList.length; i++) {
      for (let j = i + 1; j < userList.length; j++) {
        const [u1, m1] = userList[i];
        const [u2, m2] = userList[j];
        const shared = Array.from(m1).filter((m) => m2.has(m));
        if (shared.length > 0) {
          const marketInfo = activity.find((a) => a.marketId === shared[0]);
          edges.push({ u1, u2, market: marketInfo?.marketHeadline || shared[0] });
        }
      }
    }

    return (
      <div className="risk-page">
        <div className="page-header">
          <div>
            <h1>Network Analysis</h1>
            <p className="page-sub">User-market connections and activity clusters</p>
          </div>
          <button onClick={loadNetworkData} className="refresh-btn">↻ Refresh</button>
        </div>

        <div className="network-stats">
          <div className="stat-card">
            <span className="stat-label">Active Users</span>
            <span className="stat-value">{userList.length}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Connections</span>
            <span className="stat-value primary">{edges.length}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Markets</span>
            <span className="stat-value">{markets.length}</span>
          </div>
        </div>

        <div className="network-content">
          <div className="dashboard-section network-graph-section">
            <h2>Connection Graph</h2>
            <div className="network-graph-container">
              {userList.length === 0 ? (
                <div className="empty-state">No activity data to display</div>
              ) : (
                <div className="network-graph-svg">
                  {userList.map(([userId], i) => {
                    const angle = (i / Math.max(userList.length, 1)) * 2 * Math.PI - Math.PI / 2;
                    const cx = 175 + 120 * Math.cos(angle);
                    const cy = 175 + 120 * Math.sin(angle);
                    return (
                      <div
                        key={userId}
                        className="network-node"
                        style={{ left: cx, top: cy }}
                        title={topUsers.find((u) => u.userId === userId)?.username || userId}
                      >
                        <span className="node-label">
                          {(topUsers.find((u) => u.userId === userId)?.username || userId).slice(0, 8)}
                        </span>
                      </div>
                    );
                  })}
                  {edges.slice(0, 20).map((e, i) => (
                    <div key={i} className="network-edge" title={e.market} />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="dashboard-section">
            <h2>Shared Market Activity</h2>
            {edges.length === 0 ? (
              <div className="empty-state">No shared market connections</div>
            ) : (
              <div className="connections-list">
                {edges.slice(0, 15).map((e, i) => (
                  <div key={i} className="connection-item">
                    <span className="conn-users">
                      {topUsers.find((u) => u.userId === e.u1)?.username || e.u1.slice(0, 8)} ↔{' '}
                      {topUsers.find((u) => u.userId === e.u2)?.username || e.u2.slice(0, 8)}
                    </span>
                    <span className="conn-market">{e.market}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- AML Review Screen ---
  if (view === 'aml-queue') {
    return (
      <div className="risk-page">
        <div className="page-header">
          <div>
            <h1>AML Review</h1>
            <p className="page-sub">Pending withdrawals and frozen accounts for review</p>
          </div>
          <button onClick={loadAMLQueue} className="refresh-btn">↻ Refresh</button>
        </div>

        <div className="stats-grid risk-cases-stats">
          <div className="stat-card clickable" onClick={() => { setCurrentPanel('finance'); setCurrentPage('payout-queue'); }}>
            <span className="stat-label">Pending Withdrawals</span>
            <span className="stat-value orange">{withdrawals.length}</span>
            <span className="stat-hint">Require AML review</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">AML Alerts</span>
            <span className="stat-value orange">{amlAlerts.length}</span>
            <span className="stat-hint">Flagged by screening</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Frozen Accounts</span>
            <span className="stat-value danger">{frozenUsers.length}</span>
            <span className="stat-hint">Under investigation</span>
          </div>
        </div>

        <div className="risk-cases-grid">
          <div className="dashboard-section">
            <h2>AML Alerts</h2>
            {amlAlerts.length === 0 ? (
              <div className="empty-state">No AML alerts pending review</div>
            ) : (
              <div className="table-wrap">
                <table className="markets-table aml-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Risk</th>
                      <th>Trigger</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {amlAlerts.map((a) => (
                      <tr key={a.id}>
                        <td>
                          <div className="user-cell">
                            <strong>{a.username || a.userEmail || '—'}</strong>
                            <span className="muted">{a.userEmail}</span>
                          </div>
                        </td>
                        <td><span className={`risk-badge ${a.riskLevel || 'low'}`}>{a.riskLevel || '—'}</span></td>
                        <td>{a.triggerType || '—'}</td>
                        <td>{formatDate(a.createdAt)}</td>
                        <td>
                          <div className="btn-group">
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={async () => {
                                try {
                                  await apiService.reviewAmlAlert(a.id, 'cleared');
                                  loadAMLQueue();
                                } catch (e) {
                                  setError(e instanceof Error ? e.message : 'Failed');
                                }
                              }}
                            >
                              Clear
                            </button>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={async () => {
                                try {
                                  await apiService.reviewAmlAlert(a.id, 'freeze');
                                  loadAMLQueue();
                                } catch (e) {
                                  setError(e instanceof Error ? e.message : 'Failed');
                                }
                              }}
                            >
                              Freeze
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="dashboard-section">
            <h2>Pending Withdrawals</h2>
            {withdrawals.length === 0 ? (
              <div className="empty-state">No pending withdrawals</div>
            ) : (
              <div className="table-wrap">
                <table className="markets-table aml-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Amount</th>
                      <th>Created</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {withdrawals.map((w) => (
                      <tr key={w.id}>
                        <td>
                          <div className="user-cell">
                            <strong>{w.username || w.userEmail || w.userId}</strong>
                            <span className="muted">{w.userEmail}</span>
                          </div>
                        </td>
                        <td className="currency">{formatCurrency(w.amount)}</td>
                        <td>{formatDate(w.createdAt)}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => {
                              setCurrentPanel('finance');
                              setCurrentPage('payout-queue');
                            }}
                          >
                            Review
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="dashboard-section">
            <h2>Frozen Accounts (AML)</h2>
            {frozenUsers.length === 0 ? (
              <div className="empty-state">No frozen accounts</div>
            ) : (
              <div className="risk-cases-list">
                {frozenUsers.map((u) => (
                  <div key={u.id} className="risk-case-item frozen">
                    <div className="risk-case-icon">🚫</div>
                    <div className="risk-case-body">
                      <div className="risk-case-title">{u.username || u.email || u.id}</div>
                      <div className="risk-case-meta">Frozen · {u.frozen_at ? formatDate(u.frozen_at) : '—'}</div>
                    </div>
                    <div className="risk-case-actions">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => {
                          setCurrentPanel('master');
                          setCurrentPage('users-banned');
                        }}
                      >
                        Review
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- Risk Dashboard (default) ---
  return (
    <div className="risk-page">
      <div className="page-header">
        <h1>Risk Dashboard</h1>
        <button onClick={loadDashboard} className="refresh-btn">↻ Refresh</button>
      </div>

      {/* Global Stats */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <span className="stat-label">Total Pool</span>
            <span className="stat-value primary">{formatCurrency(stats.totalPoolAcrossMarkets)}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Total Liability</span>
            <span className="stat-value danger">{formatCurrency(stats.totalLiabilityAcrossMarkets)}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Live Markets</span>
            <span className="stat-value">{stats.liveMarketCount}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Active Bets</span>
            <span className="stat-value">{stats.activeBetCount}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Active Reserves</span>
            <span className="stat-value">{stats.activeReserveCount}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Pending Switches</span>
            <span className="stat-value">{stats.pendingSwitchOfferCount}</span>
          </div>
        </div>
      )}

      <div className="dashboard-grid">
        {/* Markets Risk Table */}
        <div className="dashboard-section markets-section">
          <h2>Market Risk Overview</h2>
          {markets.length === 0 ? (
            <div className="empty-state">No live markets</div>
          ) : (
            <div className="markets-table-container">
              <table className="markets-table">
                <thead>
                  <tr>
                    <th>Market</th>
                    <th>Status</th>
                    <th>Pool</th>
                    <th>Liability</th>
                    <th>Max Loss</th>
                    <th>Margin</th>
                    <th>Bets</th>
                    <th>Health</th>
                  </tr>
                </thead>
                <tbody>
                  {markets.map((market) => (
                    <tr 
                      key={market.id} 
                      className={`market-row ${market.healthStatus}`}
                      onClick={() => setSelectedMarket(selectedMarket === market.id ? null : market.id)}
                    >
                      <td className="market-headline">{market.headline}</td>
                      <td><span className={`status-badge ${market.status}`}>{market.status}</span></td>
                      <td className="currency">{formatCurrency(market.totalPool)}</td>
                      <td className="currency danger">{formatCurrency(market.totalLiability)}</td>
                      <td className="currency">{formatCurrency(market.maxPotentialLoss)}</td>
                      <td className={market.marginEstimate > 20 ? 'positive' : market.marginEstimate < 10 ? 'negative' : ''}>
                        {market.marginEstimate.toFixed(1)}%
                      </td>
                      <td>{market.activeBetCount}</td>
                      <td>{getHealthBadge(market.healthStatus)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="right-column">
          {/* Top Exposure Users */}
          <div className="dashboard-section top-users-section">
            <h2>Top Exposure</h2>
            {topUsers.length === 0 ? (
              <div className="empty-state">No active users</div>
            ) : (
              <div className="top-users-list">
                {topUsers.map((user, index) => (
                  <div key={user.userId} className="top-user-item">
                    <span className="user-rank">#{index + 1}</span>
                    <div className="user-info">
                      <span className="user-name">{user.username}</span>
                      <span className="user-bets">{user.activeBetCount} bets</span>
                    </div>
                    <div className="user-amounts">
                      <span className="staked">{formatCurrency(user.totalStaked)}</span>
                      <span className="potential">→ {formatCurrency(user.potentialPayout)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Activity */}
          <div className="dashboard-section activity-section">
            <h2>Recent Activity</h2>
            {activity.length === 0 ? (
              <div className="empty-state">No recent activity</div>
            ) : (
              <div className="activity-list">
                {activity.map((item) => (
                  <div key={item.id} className="activity-item">
                    <div className="activity-icon bet">🎲</div>
                    <div className="activity-details">
                      <span className="activity-description">{item.description}</span>
                      <span className="activity-market">{item.marketHeadline}</span>
                      <span className="activity-time">{formatDate(item.createdAt)}</span>
                    </div>
                    <span className="activity-amount">{formatCurrency(item.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskPage;
